import React, { useMemo, useRef } from 'react'
import {
  Column,
  ColumnChooser,
  ColumnChooserSearch,
  CustomRule,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Lookup,
  Paging,
  PatternRule,
  RangeRule,
  RequiredRule,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring,
} from 'devextreme-react/data-grid'
import ODataStore from 'devextreme/data/odata/store'
import { useQuery } from '@tanstack/react-query'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { useScreenSize } from '@/themes/media-query'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ad_incentivo, ad_percentuale, commissione } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  applyPendingChanges,
  notifySuccess,
  storeErrorHandler,
  validateBeforeSaving,
} from '@/routes/admin/autodesk/autodesk.utils'

export const Commissioni = () => {
  const token = useTokenRefresh()
  const { service, client } = useQsAdminApiManager()
  const dataGridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()

  const { data: incentivi } = useQuery({
    queryKey: ['ad_incentivo', 'commissioni-lookup'],
    queryFn: async () => {
      const response = await client.get(`${service.getPath()}/ad_incentivo?$select=id,nome&$orderby=nome`)
      return response.data.value as ad_incentivo[]
    },
  })

  const { data: percentuali } = useQuery({
    queryKey: ['ad_percentuale', 'commissioni-lookup'],
    queryFn: async () => {
      const response = await client.get(
        `${service.getPath()}/ad_percentuale?$select=id,percentuale&$orderby=percentuale`,
      )
      return response.data.value as ad_percentuale[]
    },
  })

  const percentualiById = useMemo(() => new Map((percentuali ?? []).map((p) => [p.id, p.percentuale])), [percentuali])

  const commissioniDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/commissione`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: storeErrorHandler,
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: ['rivenditore', 'incentivo', 'percentuale'],
    requireTotalCount: true,
  }

  const aziendeStore = new ODataStore({
    url: `${import.meta.env.VITE_QSADMINAPI_HOST}/azienda`,
    key: 'id',
    keyType: 'Int32',
    version: 4,
    beforeSend: ODataStoreRequestConfiguration(token),
    deserializeDates: false,
  })

  // Senza riga (visualizzazione) mostra tutti i rivenditori, anche archiviati, così le commissioni esistenti
  // restano leggibili; nell'editor propone solo i rivenditori attivi
  const rivenditoriDataSource = (options: { data?: Record<string, unknown> }) => ({
    store: aziendeStore,
    select: ['id', 'nome'],
    filter: options.data
      ? [['is_rivenditore', '=', true], 'and', ['archived', '<>', true]]
      : ['is_rivenditore', '=', true],
    sort: [{ selector: 'nome', desc: false }],
  })

  const percentualeAutodesk = (rowData: Partial<commissione>) =>
    rowData.percentuale?.id === undefined ? undefined : percentualiById.get(rowData.percentuale.id)

  // Valida lo stato finale delle commissioni (server + modifiche in sospeso): l'export prende la prima
  // commissione che corrisponde a rivenditore, incentivo e percentuale, quindi i duplicati vanno evitati
  const validateCommissioni = async (changes: DataGridTypes.DataChange[]) => {
    const [commissioniResponse, percentualiResponse] = await Promise.all([
      client.get(
        `${service.getPath()}/commissione?$select=id,percentuale_commissione_qs` +
          `&$expand=rivenditore($select=id),incentivo($select=id),percentuale($select=id)`,
      ),
      client.get(`${service.getPath()}/ad_percentuale?$select=id,percentuale`),
    ])
    const bands = new Map((percentualiResponse.data.value as ad_percentuale[]).map((p) => [p.id, p.percentuale]))
    const rows = applyPendingChanges<commissione>(commissioniResponse.data.value, changes)
    const tripleKey = (row: Partial<commissione>) =>
      `${row.rivenditore?.id}|${row.incentivo?.id}|${row.percentuale?.id}`

    for (const { data: row } of rows.filter((r) => r.touched)) {
      if (!row.rivenditore?.id || !row.incentivo?.id || !row.percentuale?.id)
        return 'Rivenditore, incentivo e percentuale Autodesk sono obbligatori'

      const band = bands.get(row.percentuale.id)
      if (band === undefined) return 'La percentuale Autodesk selezionata non esiste più'
      const qs = row.percentuale_commissione_qs
      if (qs === undefined || qs === null || qs < 0 || qs > band)
        return `La % commissione QS deve essere compresa tra 0 e la percentuale Autodesk (${band}%)`

      if (rows.filter((r) => tripleKey(r.data) === tripleKey(row)).length > 1) {
        const incentivo = incentivi?.find((i) => i.id === row.incentivo?.id)?.nome ?? row.incentivo.id
        return `Esiste già una commissione per lo stesso rivenditore con incentivo ${incentivo} e percentuale ${band}%`
      }
    }
  }

  return (
    <div className="management-grid content-block">
      <h2>Commissioni Autodesk</h2>

      <DataGrid
        dataSource={commissioniDataSource}
        height="75vh"
        className={currentScreenSize.isSmall ? 'grid-height-small' : 'grid-height-default'}
        noDataText="Nessuna commissione trovata"
        showBorders={false}
        remoteOperations={{
          filtering: true,
          sorting: true,
          paging: true,
        }}
        showColumnLines={true}
        focusedRowEnabled={false}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
        allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
        allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
        rowAlternationEnabled={true}
        wordWrapEnabled={true}
        onSaving={(e) => validateBeforeSaving(e, () => validateCommissioni(e.changes))}
        onRowInserted={() => notifySuccess('Created')}
        onRowUpdated={() => notifySuccess('Updated')}
        onRowRemoved={() => notifySuccess('Deleted')}
        ref={dataGridRef}
      >
        <Editing mode="batch" allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true} />

        <StateStoring enabled={true} type="localStorage" storageKey="ad-commissioni-grid" savingTimeout={50} />

        <HeaderFilter visible />
        <FilterPanel visible />
        <FilterRow visible />
        <SearchPanel visible width={currentScreenSize.isXSmall ? 200 : 250} />
        <Scrolling mode="virtual" />
        <Sorting mode="multiple" />
        <Export enabled />
        <ColumnChooser enabled>
          <ColumnChooserSearch enabled />
        </ColumnChooser>

        <Paging defaultPageSize={20} enabled />

        <Column dataField="id" caption="ID" dataType="number" width={80} allowEditing={false} visible={false} />

        <Column dataField="rivenditore.id" caption="Rivenditore" dataType="number">
          <Lookup dataSource={rivenditoriDataSource} valueExpr="id" displayExpr="nome" />
          <RequiredRule message="Rivenditore obbligatorio" />
        </Column>

        <Column dataField="incentivo.id" caption="Incentivo" dataType="number">
          <Lookup dataSource={incentivi ?? []} valueExpr="id" displayExpr="nome" />
          <RequiredRule message="Incentivo obbligatorio" />
        </Column>

        <Column dataField="percentuale.id" caption="Percentuale Autodesk (%)" dataType="number">
          <Lookup dataSource={percentuali ?? []} valueExpr="id" displayExpr="percentuale" />
          <RequiredRule message="Percentuale Autodesk obbligatoria" />
        </Column>

        <Column
          dataField="percentuale_commissione_qs"
          caption="% commissione QS"
          dataType="number"
          editorOptions={{ format: '#0', showSpinButtons: true, min: 0, max: 100 }}
        >
          <RequiredRule message="% commissione QS obbligatoria" />
          <PatternRule pattern={/^\d+$/} message="La % commissione QS deve essere un numero intero" />
          <RangeRule min={0} max={100} message="La % commissione QS deve essere compresa tra 0 e 100" />
          <CustomRule
            message="La % commissione QS non può superare la percentuale Autodesk"
            reevaluate={true}
            validationCallback={(options: { value: number; data: Partial<commissione> }) => {
              const band = percentualeAutodesk(options.data)
              return band === undefined || options.value <= band
            }}
          />
        </Column>

        <Column
          caption="% rivenditore"
          dataType="number"
          allowEditing={false}
          allowSorting={false}
          allowFiltering={false}
          calculateCellValue={(rowData: Partial<commissione>) => {
            const band = percentualeAutodesk(rowData)
            const qs = rowData.percentuale_commissione_qs
            return band === undefined || qs === undefined || qs === null ? undefined : band - qs
          }}
          hidingPriority={1}
        />
      </DataGrid>
    </div>
  )
}
